'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Spinner, TextArea } from '@common/components';
import { S3_PATH_TYPE } from '@common/constants/s3';
import { useToast } from '@common/hooks';
import { SvgCameraFill } from '@common/icon';
import { cn } from '@core/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AuthApi, AuthMutation, UnauthQueryKey, UnauthQueryOptions } from '@/api';
import { FileUploadPlusBox } from '@/app/_components/FileUploadPlusBox';
import { useFileUpload } from '@/hooks/useFileUpload';
import { closeResponsiveDialog } from '@/stores/DrawerStore';
import type { FestivalReview } from '../../_utils/festivalFetchers';
import { FESTIVAL_SECTION_IDS } from '../_constants/sectionIds';
type SaveTourReviewBody = NonNullable<Parameters<(typeof AuthApi)['post:/wello/v1/auth/tour/festival/{tourId}/review']>[0]>['body'];
type GoodPointName = SaveTourReviewBody['goodPoints'][number];
const MAX_IMAGE_COUNT = 5;
const MAX_IMAGE_MEGABYTE = 30;
const MAX_IMAGE_BYTE = MAX_IMAGE_MEGABYTE * 1024 * 1024;
interface FestivalReviewWriteFormProps {
  tourId: string;
  /** 있으면 수정 모드로 동작 */
  review?: FestivalReview;
}
export const FestivalReviewWriteForm = t0 => {
  const $ = _c(76);
  const {
    tourId,
    review
  } = t0;
  const queryClient = useQueryClient();
  const {
    toast,
    toastError
  } = useToast();
  const reviewId = review?.reviewId;
  let t1;
  if ($[0] !== review?.goodPoints) {
    t1 = () => review?.goodPoints?.map(_temp).filter(_temp2) ?? [];
    $[0] = review?.goodPoints;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [selectedGoodPoints, setSelectedGoodPoints] = useState(t1);
  const [contents, setContents] = useState(review?.contents ?? "");
  let t2;
  if ($[2] !== review?.imageUrls) {
    t2 = review?.imageUrls ?? [];
    $[2] = review?.imageUrls;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [imageUrls, setImageUrls] = useState(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = UnauthQueryOptions["get:/wello/v1/allows/tour/review/good-point-types"]();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: goodPointTypesResponse
  } = useQuery(t3);
  let t4;
  if ($[5] !== goodPointTypesResponse?.context) {
    t4 = goodPointTypesResponse?.context ?? [];
    $[5] = goodPointTypesResponse?.context;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const goodPointTypes = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      accept: "image/jpeg,image/gif,image/png,image/heic,image/heif,.heic,.heif",
      multiple: true
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    getFiles
  } = useFileUpload(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = t7 => {
      const {
        data
      } = t7;
      const uploadedUrls = data?.context?.contents?.map(_temp4).filter(_temp5) ?? [];
      setImageUrls(previousUrls => [...previousUrls, ...uploadedUrls].slice(0, MAX_IMAGE_COUNT));
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== toastError) {
    t7 = {
      mutationFn: _temp3,
      onSuccess: t6,
      onError: error => toastError({
        error
      })
    };
    $[9] = toastError;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const {
    mutate: uploadImages,
    isPending: isUploading
  } = useMutation(t7);
  let t8;
  if ($[11] !== queryClient || $[12] !== reviewId || $[13] !== toast || $[14] !== tourId) {
    t8 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: UnauthQueryKey["get:/wello/v1/allows/tour/festival/{tourId}/review"]({
          path: {
            tourId
          }
        })
      }), queryClient.invalidateQueries({
        queryKey: UnauthQueryKey["get:/wello/v1/allows/tour/festival/{tourId}"]({
          path: {
            tourId
          }
        })
      })]);
      toast({
        message: reviewId ? "\uD6C4\uAE30\uAC00 \uC218\uC815\uB418\uC5C8\uC5B4\uC694." : "\uD6C4\uAE30\uAC00 \uB4F1\uB85D\uB418\uC5C8\uC5B4\uC694."
      });
      closeResponsiveDialog();
      document.getElementById(FESTIVAL_SECTION_IDS.review)?.scrollIntoView();
    };
    $[11] = queryClient;
    $[12] = reviewId;
    $[13] = toast;
    $[14] = tourId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleSaveSuccess = t8;
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = AuthMutation["post:/wello/v1/auth/tour/festival/{tourId}/review"]();
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== toastError) {
    t10 = error_0 => toastError({
      error: error_0
    });
    $[17] = toastError;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== handleSaveSuccess || $[20] !== t10) {
    t11 = {
      ...t9,
      onSuccess: handleSaveSuccess,
      onError: t10
    };
    $[19] = handleSaveSuccess;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const {
    mutate: createReview,
    isPending: isCreating
  } = useMutation(t11);
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = AuthMutation["put:/wello/v1/auth/tour/festival/{tourId}/review/{reviewId}"]();
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== toastError) {
    t13 = error_1 => toastError({
      error: error_1
    });
    $[23] = toastError;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== handleSaveSuccess || $[26] !== t13) {
    t14 = {
      ...t12,
      onSuccess: handleSaveSuccess,
      onError: t13
    };
    $[25] = handleSaveSuccess;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const {
    mutate: updateReview,
    isPending: isUpdating
  } = useMutation(t14);
  const isSaving = isCreating || isUpdating;
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = goodPointName => {
      setSelectedGoodPoints(previousGoodPoints => previousGoodPoints.includes(goodPointName) ? previousGoodPoints.filter(name_0 => name_0 !== goodPointName) : [...previousGoodPoints, goodPointName]);
    };
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const toggleGoodPoint = t15;
  let t16;
  if ($[29] !== getFiles || $[30] !== imageUrls.length || $[31] !== toast || $[32] !== uploadImages) {
    t16 = async () => {
      const files_0 = await getFiles();
      if (files_0.length === 0) {
        return;
      }
      const remainingImageCount = MAX_IMAGE_COUNT - imageUrls.length;
      if (files_0.length > remainingImageCount) {
        toast({
          message: `사진은 ${remainingImageCount}장까지 더 첨부할 수 있어요.`,
          type: "warning"
        });
        return;
      }
      if (files_0.some(_temp6)) {
        toast({
          message: `파일 용량은 최대 ${MAX_IMAGE_MEGABYTE}MB까지 가능합니다.`,
          type: "warning"
        });
        return;
      }
      uploadImages(files_0);
    };
    $[29] = getFiles;
    $[30] = imageUrls.length;
    $[31] = toast;
    $[32] = uploadImages;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const handleAttachImages = t16;
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h3 className="m-0 font-700 text-Gray-900 typo-body2">이런 점이 좋았어요<span className="text-R-500">*</span></h3>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== goodPointTypes || $[36] !== selectedGoodPoints) {
    let t19;
    if ($[38] !== selectedGoodPoints) {
      t19 = goodPointType => {
        const goodPointName_0 = goodPointType.name as GoodPointName | undefined;
        if (!goodPointName_0 || !goodPointType.desc) {
          return null;
        }
        const isSelected = selectedGoodPoints.includes(goodPointName_0);
        return <button key={goodPointName_0} aria-pressed={isSelected} onClick={() => toggleGoodPoint(goodPointName_0)} className={cn("flex items-center rounded-xxsmall border border-Gray-200 px-16 py-12 text-left font-600 text-Gray-900 transition-colors typo-body4", isSelected && "border-Gray-900")}>{goodPointType.desc}</button>;
      };
      $[38] = selectedGoodPoints;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    t18 = goodPointTypes.map(t19);
    $[35] = goodPointTypes;
    $[36] = selectedGoodPoints;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <section className="flex flex-col gap-12">{t17}<div className="grid grid-cols-2 gap-8">{t18}</div></section>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <h3 className="m-0 font-700 text-Gray-900 typo-body2">짧게라도 좋아요, 후기를 남겨주세요</h3>;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = event => setContents(event.target.value);
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== contents) {
    t22 = <TextArea value={contents} placeholder={"\uB0B4\uC6A9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."} maxLength={1000} rows={4} className="h-auto w-full" onChange={t21} />;
    $[44] = contents;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== imageUrls) {
    t23 = imageUrls.length > 0 ? <ul className="m-0 flex list-none flex-wrap gap-8 p-0">{imageUrls.map((imageUrl, imageIndex) => <li key={`${imageUrl}-${imageIndex}`}><FileUploadPlusBox type={S3_PATH_TYPE.WELLO_TOUR_REVIEW} imageUrl={imageUrl} previewBlank={true} onDelete={() => setImageUrls(previousUrls_0 => previousUrls_0.filter((_, urlIndex) => urlIndex !== imageIndex))} /></li>)}</ul> : null;
    $[46] = imageUrls;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== handleAttachImages || $[49] !== imageUrls.length || $[50] !== isUploading) {
    t24 = imageUrls.length < MAX_IMAGE_COUNT ? <button disabled={isUploading} className="flex w-full flex-col items-center justify-center gap-4 rounded-xsmall border border-dashed border-Gray-400 bg-Gray-100 py-24 transition-colors hover:bg-Gray-100/70" onClick={handleAttachImages}><span className="flex items-center gap-4 font-600 text-Gray-900 typo-body4">{isUploading ? <Spinner size={14} /> : <SvgCameraFill className="size-18" />}사진 첨부하기</span><span className="text-Gray-700 typo-caption1">최대 {MAX_IMAGE_COUNT}장까지 첨부할 수 있어요</span></button> : null;
    $[48] = handleAttachImages;
    $[49] = imageUrls.length;
    $[50] = isUploading;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <ul className="m-0 flex flex-col p-0 pl-16 text-Gray-600 typo-caption1"><li className="list-disc">사진 형식은 파일(jpg, jpeg, gif, png, heic, heif)로 제출 가능합니다.</li><li className="list-disc">파일 용량은 최대 {MAX_IMAGE_MEGABYTE}MB까지 가능합니다.</li></ul>;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t23 || $[54] !== t24) {
    t26 = <div className="flex flex-col gap-8">{t23}{t24}{t25}</div>;
    $[53] = t23;
    $[54] = t24;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t22 || $[57] !== t26) {
    t27 = <section className="flex flex-col gap-12">{t20}{t22}{t26}</section>;
    $[56] = t22;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  const t28 = selectedGoodPoints.length === 0 || isUploading;
  let t29;
  if ($[59] !== contents || $[60] !== createReview || $[61] !== imageUrls || $[62] !== reviewId || $[63] !== selectedGoodPoints || $[64] !== tourId || $[65] !== updateReview) {
    t29 = () => {
      const body = {
        goodPoints: selectedGoodPoints,
        contents: contents.trim() || undefined,
        imageUrls: imageUrls.length > 0 ? imageUrls : undefined
      };
      if (reviewId) {
        updateReview({
          path: {
            tourId,
            reviewId
          },
          body
        });
      } else {
        createReview({
          path: {
            tourId
          },
          body
        });
      }
    };
    $[59] = contents;
    $[60] = createReview;
    $[61] = imageUrls;
    $[62] = reviewId;
    $[63] = selectedGoodPoints;
    $[64] = tourId;
    $[65] = updateReview;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  const t30 = reviewId ? "\uC218\uC815\uD558\uAE30" : "\uB4F1\uB85D\uD558\uAE30";
  let t31;
  if ($[67] !== isSaving || $[68] !== t28 || $[69] !== t29 || $[70] !== t30) {
    t31 = <Button theme="primary-fill" dimensions="H56" className="w-full" disabled={t28} loading={isSaving} onClick={t29}>{t30}</Button>;
    $[67] = isSaving;
    $[68] = t28;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t19 || $[73] !== t27 || $[74] !== t31) {
    t32 = <div className="flex flex-col gap-32 text-start">{t19}{t27}{t31}</div>;
    $[72] = t19;
    $[73] = t27;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  return t32;
};
function _temp(goodPoint) {
  return goodPoint.name as GoodPointName | undefined;
}
function _temp2(name) {
  return Boolean(name);
}
function _temp3(files) {
  return AuthApi["post:/wello/v2/auth/s3"]({
    body: {
      fileUrlList: files,
      s3CdnTypeCd: "S3_CDN_TYPE_WELFAREHELLO",
      type: S3_PATH_TYPE.WELLO_TOUR_REVIEW
    }
  });
}
function _temp4(content) {
  return content.fileUrl;
}
function _temp5(fileUrl) {
  return Boolean(fileUrl);
}
function _temp6(file) {
  return file.size > MAX_IMAGE_BYTE;
}